'use client';

import type { QueryKey } from '@tanstack/react-query';

export type PrV2CacheSource =
  | 'cache:unknown'
  | 'server:initial-data'
  | 'seed:inbox-click'
  | 'prefetch:background'
  | 'prefetch:hover'
  | 'prefetch:keyboard'
  | 'prefetch:intent';

const cacheSourceByQueryKey = new Map<string, PrV2CacheSource>();
const loggedPrefetchEvents = new Set<string>();

function serializeQueryKey(queryKey: QueryKey): string {
  return JSON.stringify(queryKey);
}

function logOnce(eventKey: string, message: string) {
  if (loggedPrefetchEvents.has(eventKey)) {
    return;
  }

  loggedPrefetchEvents.add(eventKey);
  console.warn(message);
}

export function setPrV2CacheSource(queryKey: QueryKey, source: PrV2CacheSource): void {
  cacheSourceByQueryKey.set(serializeQueryKey(queryKey), source);
}

export function getPrV2CacheSource(queryKey: QueryKey): PrV2CacheSource | null {
  return cacheSourceByQueryKey.get(serializeQueryKey(queryKey)) ?? null;
}

export function logPrV2CacheUsage({
  target,
  prKey,
  queryKey,
  source,
}: {
  target: 'summary' | 'code-changes';
  prKey: string;
  queryKey: QueryKey;
  source: PrV2CacheSource;
}) {
  logOnce(
    `cache:${serializeQueryKey(queryKey)}:${source}`,
    `[PREFETCH] use-cache ${target} ${prKey} source=${source}`,
  );
}

export function logPrV2InitialDataUsage({
  target,
  prKey,
  queryKey,
  source,
}: {
  target: 'summary' | 'code-changes';
  prKey: string;
  queryKey: QueryKey;
  source: Extract<PrV2CacheSource, 'server:initial-data'>;
}) {
  logOnce(
    `initial-data:${serializeQueryKey(queryKey)}:${source}`,
    `[PREFETCH] use-initial-data ${target} ${prKey} source=${source}`,
  );
}
